import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import queryString from 'query-string';

import {
  useCategories,
  useFeatureFlags,
  useMyAccount,
  useNavigateTo,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import {
  createProductSearchQueryFromFilters,
  getNewSelectedFilters,
  getQueryParametersFromUrl,
  getSeoName
} from '../../../shared/utils/filters/filters';
import {
  formatTireSizeParts,
  formatWheelSizeParts
} from '../../../shared/utils/format/format';
import { getFacetsAndFilters } from '../utils/facets';
import {
  getDescription,
  getPageBreadcrumbs,
  getTireSizes,
  getTitle,
  getWheelSizes,
  updateSuggestedSellingSearchedStatus
} from '../utils/navigation';

import { FEATURE_FLAGS } from '../../../shared/constants/feature-flags';
import { PRODUCT_TYPES, QUERY_TYPES } from '../../../shared/constants/products';
import { SORT_PARAMS } from '../../../shared/constants/search-types';

export default function usePlpNavigation(
  productType,
  queryType,
  selectedFitment,
  baseSortValue = 'bestSeller-asc'
) {
  const navigateTo = useNavigateTo();
  const history = useHistory();
  const location = useLocation();
  const [initialFilters, setInitialFilters] = useState();
  const [initialFacets, setInitialFacets] = useState();
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filters, setFilters] = useState([]);
  const [queryParameters, setQueryParameters] = useState();
  const [breadcrumbs, setBreadcrumbs] = useState();
  const [category, setCategory] = useState();
  const params = useParams();
  const [routeParams, setRouteParams] = useState(params);
  const [isSizeBrandSet, setIsSizeBrandSet] = useState(false);
  const { isTreadwellFlow } = useTreadwellPreferences();
  const { featureFlags } = useFeatureFlags();
  const { isFleetAccount } = useMyAccount();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;

  const queryParams = useMemo(
    () => queryString.parse(location.search),
    [location.search]
  );

  useEffect(() => {
    if (!isEqual(params, routeParams)) {
      setRouteParams(params);
    }
  }, [params, routeParams]);

  let seoName;
  if (
    queryType === QUERY_TYPES.BRAND_SEARCH ||
    queryType === QUERY_TYPES.CATEGORY_SEARCH
  ) {
    seoName = getSeoName(routeParams?.[0], PRODUCT_TYPES[productType]);
  }

  const { category: categoryData, categoryData: subCategoryData } =
    useCategories({
      includeNoFitCategories: false,
      includeProducts: false,
      isTaxonomy: false,
      productType,
      seoName
    });

  const { brand, rearSize, size, staggeredSearchAxle } = routeParams;
  const { boltPattern, diameter, width } = queryParams;

  const tireSizes = useMemo(
    () => getTireSizes({ productType, rearSize, size }),
    [size, rearSize, productType]
  );

  const wheelSizes = useMemo(
    () =>
      getWheelSizes({
        boltPattern,
        diameter,
        productType,
        rearSize,
        size,
        width
      }),
    [size, rearSize, diameter, width, boltPattern, productType]
  );

  let subtitle = null;

  if (queryParams.cat && Array.isArray(subCategoryData)) {
    const subCat = subCategoryData.find(c => c.seoName === queryParams.cat);
    if (subCat) subtitle = `<p>${subCat.description}</p>`;
  } else if (queryType === QUERY_TYPES.SIZE_SEARCH) {
    if (productType === PRODUCT_TYPES.TIRES && !tireSizes?.rear) {
      const formattedTireSize = formatTireSizeParts(tireSizes?.front);
      subtitle = `<p>Our selection of <strong>${formattedTireSize} tires</strong> includes the best brands at even better prices. Find the perfect ${formattedTireSize} tires for your vehicle right here. Sort by brand, price, reviews, and more!</p>`;
    } else if (productType === PRODUCT_TYPES.WHEELS && !wheelSizes?.rear) {
      const formattedWheelSize = formatWheelSizeParts(wheelSizes?.front);
      subtitle = `<p>Our selection of <strong>${formattedWheelSize} rims</strong> includes the best wheel brands at even better prices. Find the perfect ${formattedWheelSize} wheels for your vehicle right here. Sort by brand, style, color, price, and more!</p>`;
    }
  }

  const search = location.search;

  const updatePageQuery = useCallback(
    (queryParams, dontNavigate) => {
      const {
        boltPattern = '',
        brand = '',
        currentSearchOption = '',
        diameter = '',
        pageNum = 0,
        query = '',
        searchText = '',
        sort = '',
        width = ''
      } = queryParams || {};

      const pageQuery = `?q=${encodeURIComponent(query)}${
        sort ? `&sort=${sort}` : ''
      }${
        currentSearchOption ? `&currentSearchOption=${currentSearchOption}` : ''
      }&page=${pageNum}${brand ? `&brand=${brand}` : ''}${
        searchText ? `&text=${searchText}` : ''
      }${diameter ? `&diameter=${diameter}` : ''}${
        width ? `&width=${width}` : ''
      }${boltPattern ? `&boltPattern=${boltPattern}` : ''}`;

      if (history && history.location) {
        const newUrl = `${history.location.pathname}${pageQuery}`;
        if (dontNavigate) {
          return newUrl;
        }
        navigateTo(newUrl, false, { shouldPreventScroll: true });
      }
    },
    [history, navigateTo]
  );

  function getPagenationUrl(pageNumber) {
    queryParams.pageNum = pageNumber;
    return updatePageQuery(queryParams, true);
  }

  const updateQueryParams = useCallback(
    (params = {}) => {
      const newQueryParameters = {
        ...queryParameters,
        ...params
      };

      setQueryParameters(newQueryParameters);
      updatePageQuery(newQueryParameters);
    },
    [updatePageQuery, queryParameters]
  );

  const setSort = useCallback(
    (sort, filters, currentSearchOption) => {
      updateQueryParams({
        currentSearchOption,
        query: createProductSearchQueryFromFilters(
          filters,
          sort,
          queryParameters?.searchText
        ),
        sort
      });
    },
    [queryParameters, updateQueryParams]
  );

  const setPageNum = useCallback(
    pageNum => {
      updateQueryParams({ pageNum });
    },
    [updateQueryParams]
  );

  const setQueryFromFilters = useCallback(
    filters => {
      const showAllTiresSelected = filters.some(
        v =>
          v.categoryCode === 'all-tire-boolean' &&
          queryParameters?.sort === SORT_PARAMS.RECOMMENDED
      );
      const allTiresSort = featureFlags?.[
        FEATURE_FLAGS.__FF_BEST_AVAILABLE_SORT__
      ]
        ? SORT_PARAMS.BEST_AVAILABLE_ASC
        : SORT_PARAMS.BEST_SELLER_ASC;

      updateQueryParams({
        pageNum: 0,
        query: createProductSearchQueryFromFilters(
          filters,
          showAllTiresSelected ? allTiresSort : queryParameters?.sort,
          queryParameters?.searchText
        ),
        sort: showAllTiresSelected ? allTiresSort : queryParameters?.sort
      });
    },
    [updateQueryParams, queryParameters, featureFlags]
  );

  const selectFilters = useCallback(
    filterToToggle => {
      const isShowAllTiresSelected = selectedFilters.some(
        v =>
          (v.categoryCode === 'all-tire-boolean' &&
            filterToToggle.categoryCode === 'all-tire-boolean') ||
          (v.categoryCode === 'fleet-pref-boolean' &&
            filterToToggle.categoryCode === 'fleet-pref-boolean')
      );
      if (
        isTreadwellFlow &&
        queryParameters?.sort === SORT_PARAMS.BEST_SELLER_ASC &&
        isShowAllTiresSelected
      ) {
        queryParameters.sort = SORT_PARAMS.RECOMMENDED;
      }

      if (
        isFleetEnabled &&
        isTreadwellFlow &&
        queryParameters?.sort === SORT_PARAMS.RECOMMENDED &&
        isShowAllTiresSelected
      ) {
        queryParameters.sort = SORT_PARAMS.BEST_SELLER_ASC;
      }

      if (isSizeBrandSet && routeParams) {
        const brandNameFromParams = brand || staggeredSearchAxle;
        const newUrl = history?.location?.pathname.replace(
          `/${brandNameFromParams}`,
          ''
        );
        navigateTo(newUrl);
      }
      setQueryFromFilters(
        getNewSelectedFilters(
          filterToToggle,
          selectedFilters,
          queryParameters?.sort
        )
      );
    },
    [
      selectedFilters,
      isFleetEnabled,
      isTreadwellFlow,
      queryParameters,
      isSizeBrandSet,
      routeParams,
      setQueryFromFilters,
      brand,
      staggeredSearchAxle,
      history?.location?.pathname,
      navigateTo
    ]
  );

  const clearSelectedFilters = useCallback(() => {
    setSelectedFilters([]);
    setQueryFromFilters([]);
  }, [setQueryFromFilters, setSelectedFilters]);

  useEffect(() => {
    const queryParams = queryString.parse(search);
    const { facets, filters } = getFacetsAndFilters(
      queryParams,
      routeParams,
      queryType,
      productType
    );
    //TODO: Temporary fix - Need to figure out a better way to sync initial facets and query changes. Also need to determine the significance of initial facets.
    !initialFacets && setInitialFacets(facets);
    setInitialFilters(filters);

    const queryParameters = getQueryParametersFromUrl(
      queryParams,
      baseSortValue
    );

    setQueryParameters(queryParameters);

    updateSuggestedSellingSearchedStatus(productType, queryParams);

    if (
      queryType !== QUERY_TYPES.BRAND_SEARCH &&
      queryType !== QUERY_TYPES.CATEGORY_SEARCH
    ) {
      setBreadcrumbs(
        getPageBreadcrumbs({
          categoryData,
          productType,
          queryParams,
          queryType,
          routeParams
        })
      );
    }
  }, [
    initialFacets,
    search,
    categoryData,
    productType,
    queryType,
    routeParams,
    baseSortValue
  ]);

  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    if (!categoryData) {
      setCategory(null);

      return;
    }

    updateSuggestedSellingSearchedStatus(productType, queryParams);

    if (
      queryType === QUERY_TYPES.BRAND_SEARCH ||
      queryType === QUERY_TYPES.CATEGORY_SEARCH
    ) {
      setBreadcrumbs(
        getPageBreadcrumbs({
          categoryData,
          productType,
          queryParams,
          queryType,
          routeParams
        })
      );
      setCategory(categoryData);
    }
  }, [location, categoryData, productType, queryType, routeParams]);

  useEffect(() => {
    if (!isSizeBrandSet && (params?.brand || params?.staggeredSearchAxle)) {
      const brandNameFromParams = params?.brand || params?.staggeredSearchAxle;
      const brandName = brandNameFromParams?.split('-').join(' ').toLowerCase();
      const brandFilters = filters?.find(
        filter => filter.categoryCode === 'brands'
      );
      const matchedBrandFilter = brandFilters?.values?.find(
        value => value?.valueName?.toLowerCase() === brandName
      );

      if (matchedBrandFilter) {
        setIsSizeBrandSet(true);
        selectFilters(matchedBrandFilter);
      }
    }
  }, [
    filters,
    isSizeBrandSet,
    params?.brand,
    params?.staggeredSearchAxle,
    selectFilters
  ]);

  return {
    breadcrumbs,
    category,
    clearSelectedFilters,
    filters,
    getPagenationUrl,
    initialFacets,
    initialFilters,
    metaData: {
      description: getDescription({
        brand: brand || staggeredSearchAxle,
        category,
        productType,
        queryParams,
        queryType,
        selectedFilters,
        selectedFitment,
        tireSizes,
        wheelSizes
      }),
      title: getTitle({
        brand: brand || staggeredSearchAxle,
        category,
        productType,
        queryParams,
        queryType,
        selectedFitment,
        tireSizes,
        wheelSizes
      })
    },
    queryParameters,
    selectFilters,
    selectedFilters,
    setFilters,
    setPageNum,
    setSelectedFilters,
    setSort,
    subtitle,
    tireSizes,
    updateQueryParams,
    wheelSizes
  };
}
